import type {
  LoveWinsAllAction,
  LoveWinsAllState,
  LoveWinsAllViewerState,
} from "@cloudnest/love-wins-all-engine";
import {
  filterStateForViewer,
  validateAndReduceGameState,
} from "@cloudnest/love-wins-all-engine";
import { loveWinsAllEventToAction } from "./event-adapter";
import type { LoveWinsAllReplayFixtureEvent } from "./replay-fixtures.generated";

/**
 * UI-free 러브 윈즈 올 (love-wins-all) replay simulation. `init` seeds canonical
 * state; every other event is reconstructed via the event adapter and folded
 * through the engine reducer. Fails loudly on unsupported/rejected events.
 *
 * Hidden information: each snapshot carries the canonical `state` (for the recap
 * toggle) and an observer `viewer` projection where both players' hands are
 * masked (count-only) and the deck/rng are dropped.
 */
export interface LoveWinsAllReplaySnapshot {
  readonly index: number;
  readonly event: LoveWinsAllReplayFixtureEvent;
  readonly action: LoveWinsAllAction | null;
  readonly state: LoveWinsAllState;
  readonly viewer: LoveWinsAllViewerState;
}

function requireState(
  state: LoveWinsAllState | null,
  eventType: string,
): LoveWinsAllState {
  if (state === null) throw new Error(`${eventType} before init`);
  return state;
}

function readInitState(payload: Record<string, unknown>): LoveWinsAllState {
  const candidate = (payload as { state?: unknown }).state;
  if (typeof candidate !== "object" || candidate === null) {
    throw new Error("love-wins-all init event is missing a state snapshot");
  }
  return candidate as LoveWinsAllState;
}

function snapshot(
  index: number,
  event: LoveWinsAllReplayFixtureEvent,
  action: LoveWinsAllAction | null,
  state: LoveWinsAllState,
): LoveWinsAllReplaySnapshot {
  return {
    index,
    event,
    action,
    state,
    viewer: filterStateForViewer(state, null),
  };
}

export function buildLoveWinsAllReplaySnapshots(
  events: readonly LoveWinsAllReplayFixtureEvent[],
): readonly LoveWinsAllReplaySnapshot[] {
  const snapshots: LoveWinsAllReplaySnapshot[] = [];
  let state: LoveWinsAllState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = readInitState(event.payload);
      snapshots.push(snapshot(snapshots.length, event, null, state));
      continue;
    }

    const current = requireState(state, event.eventType);
    const action = loveWinsAllEventToAction(event.eventType, event.payload);
    const result = validateAndReduceGameState(current, action);
    if (!result.accepted) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.error ?? "Validation failed"
        }`,
      );
    }
    state = result.state;
    snapshots.push(snapshot(snapshots.length, event, action, state));
  }

  if (state === null) {
    throw new Error("Cannot replay an empty love-wins-all event log");
  }
  return snapshots;
}
